import { differenceInCalendarDays, startOfDay } from 'date-fns';
import { useMemo } from 'react';

import { type CreditAnalyticsParams } from '@air/analytics';
import { MONTHS_IN_YEAR } from '@air/utilities';

import { useSubscription } from '~/swr-hooks/subscriptions/useSubscription';
import { useCreditStats } from '~/swr-hooks/workspaces/useCreditStats';

const PERCENT_MULTIPLIER = 100;

const getCycleStartDate = (cycleStartDay: number, now: Date): Date => {
  const thisYear = now.getFullYear();
  const thisMonth = now.getMonth();
  const lastDayOfThisMonth = new Date(thisYear, thisMonth + 1, 0).getDate();
  const cycleStartedThisMonth = now.getDate() >= Math.min(cycleStartDay, lastDayOfThisMonth);

  const prevYear = !cycleStartedThisMonth && thisMonth === 0 ? thisYear - 1 : thisYear;
  const prevMonth = cycleStartedThisMonth ? thisMonth : (thisMonth + MONTHS_IN_YEAR - 1) % MONTHS_IN_YEAR;
  const lastDayOfPrevMonth = new Date(prevYear, prevMonth + 1, 0).getDate();

  return startOfDay(new Date(prevYear, prevMonth, Math.min(cycleStartDay, lastDayOfPrevMonth)));
};

export const useCreditAnalyticsParams = () => {
  const { data: creditStats } = useCreditStats();
  const { data: subscription } = useSubscription();

  return useMemo<CreditAnalyticsParams | null>(() => {
    if (!subscription || !creditStats?.allocated || creditStats.remaining == null) {
      return null;
    }

    const pctUsed = Math.round((creditStats.used.total / creditStats.allocated) * PERCENT_MULTIPLIER);

    let daysSinceCycleStart = 0;

    if (subscription.cycleStartDay) {
      const now = new Date();
      const cycleStartDate = getCycleStartDate(subscription.cycleStartDay, now);
      daysSinceCycleStart = Math.max(0, differenceInCalendarDays(now, cycleStartDate));
    }

    return {
      plan: subscription.plan,
      existing_pack_quantity: subscription.creditPackQuantity,
      credit_pack_change_pending: !!subscription.pendingPackChange,
      credits_remaining: creditStats.remaining,
      pct_used: pctUsed,
      credits_used: creditStats.used.total,
      days_since_cycle_start: daysSinceCycleStart,
    };
  }, [creditStats, subscription]);
};
